/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Feature } from 'ol';
import { Collection } from 'ol';
import { always, never } from 'ol/events/condition';
import OlGeoJSON from 'ol/format/GeoJSON';
import type { Circle, Geometry, Polygon } from 'ol/geom';
import { fromCircle } from 'ol/geom/Polygon';
import type { Interaction } from 'ol/interaction';
import { DragPan, Draw, Modify, Snap, Translate } from 'ol/interaction';
import type { DrawEvent, Options as DrawOptions } from 'ol/interaction/Draw';
import { createBox, createRegularPolygon } from 'ol/interaction/Draw';
import type { ModifyEvent } from 'ol/interaction/Modify';
import VectorLayer from 'ol/layer/Vector';
import type { Pixel } from 'ol/pixel';
import VectorSource from 'ol/source/Vector';
import type { StyleLike } from 'ol/style/Style';
import Style from 'ol/style/Style';
import React from 'react';
import MapContext from '../context/MapContext';
import {
  createBoxBasedOnModifiedVertex,
  drawStyles,
  createOval,
  FEATURE_VERTICE_HANDLE_IMAGE,
  getBoxRotateHandlePoints,
  modifyStyles,
  splitStyle,
} from '../OlStyles';
import {
  NEW_FEATURE_CREATED,
  roundCoordinates,
  splitLine,
  splitPolygon,
} from './mapDrawUtils';
import type { DrawModeExitCallback, DrawModeValue } from './types';
import { DRAWMODE } from './types';
import { isClickFromDrag, isGeoJSONGeometryEmpty } from './utils';
import { useProjection } from '../utils/projections';

export interface OpenLayersMapDrawProps {
  geojson: GeoJSON.FeatureCollection;
  drawMode?: DrawModeValue;
  viewOnlyStyle: StyleLike;
  exitDrawModeCallback?: (reason: DrawModeExitCallback) => void;
  isInEditMode: boolean;
  selectedFeatureIndex: number;
  updateGeojson?: (geoJson: GeoJSON.FeatureCollection, text: string) => void;
  onViewOnlyFeatureClick?: (feature: Feature<Geometry>[]) => void;
  onNoFeatureClick?: () => void;
}

const createDrawOperation = (
  type: DrawModeValue,
  selectedGeometryType?: GeoJSON.Geometry['type'],
): DrawOptions => {
  switch (type) {
    case 'OVAL':
      return {
        type: 'Circle',
        geometryFunction: createOval(),
      };
    case 'CIRCLE':
      return {
        type: 'Circle',
      };
    case 'SQUARE':
      return {
        type: 'Circle',
        geometryFunction: createRegularPolygon(4),
      };
    case 'BOX':
    case 'ROTATABLEBOX':
      return {
        type: 'Circle',
        geometryFunction: createBox(),
      };
    case 'POINT':
    case 'LOCATION':
      return {
        type: 'Point',
      };
    case 'LINESTRING':
      return {
        type: 'LineString',
      };
    case 'SMOOTHLINE':
      return {
        type: 'LineString',
      };
    case 'FREEHANDLINE':
      return {
        type: 'LineString',
        freehand: true,
      };
    case 'SPLIT':
      return {
        type: selectedGeometryType === 'LineString' ? 'LineString' : 'Polygon',
        trace: selectedGeometryType === 'Polygon',
        style: splitStyle,
      };
    case 'POLYGON':
    default:
      return {
        type: 'Polygon',
        // This prevents OL default doubleClick behavior, keeps edit mode active
        finishCondition: () => false,
      };
  }
};

const modifyHandleStyle = new Style({
  image: FEATURE_VERTICE_HANDLE_IMAGE,
});

const selectionTypeToDrawModeValue = (
  selectionType:
    | 'box'
    | 'fir'
    | 'poly'
    | 'polygon'
    | 'point'
    | 'linestring'
    | 'custom-line'
    | 'delete'
    | 'freehand-line'
    | 'smooth-line'
    | 'square'
    | 'circle'
    | 'oval'
    | 'location'
    | 'rotatable-box',
): DrawModeValue => {
  switch (selectionType) {
    case 'circle':
      return DRAWMODE.CIRCLE;
    case 'square':
      return DRAWMODE.SQUARE;
    case 'box':
      return DRAWMODE.BOX;
    case 'rotatable-box':
      return DRAWMODE.ROTATABLEBOX;
    case 'oval':
      return DRAWMODE.OVAL;
    case 'point':
      return DRAWMODE.POINT;
    case 'location':
      return DRAWMODE.LOCATION;

    case 'linestring':
    case 'custom-line':
      return DRAWMODE.LINESTRING;
    case 'freehand-line':
      return DRAWMODE.FREEHANDLINE;
    case 'smooth-line':
      return DRAWMODE.SMOOTHLINE;

    case 'delete':
      return 'DELETE';

    case 'fir':
    case 'poly':
    case 'polygon':
    default:
      return DRAWMODE.POLYGON;
  }
};

/**
 * Integrates with OpenLayers Draw and Modify (+Snap) interactions, to implement feature drawing
 * and modification behaviour.
 *
 * There is a corner case where this fails: the user will only be able to edit the existing
 * feature (not add) when there are 1) multiple features in the geojson, and 2) the
 * selectedFeatureIndex points to a feature with of the same type as drawMode is going to draw. To
 * fix this properly, it would be better that the surrounding code would add a placeholder feature
 * in the geojson and then activate drawing at that feature index. This means the openlayers version
 * works slightly differently from the WebMapJS version, but since the multi-feature edit feature
 * is not really a requirement for any production workflows, this functionality was left as is.
 */
export const OpenLayersMapDraw: React.FC<OpenLayersMapDrawProps> = ({
  isInEditMode,
  drawMode,
  viewOnlyStyle,
  geojson,
  selectedFeatureIndex,
  exitDrawModeCallback,
  updateGeojson,
  onViewOnlyFeatureClick,
  onNoFeatureClick,
}: OpenLayersMapDrawProps) => {
  const { map } = React.useContext(MapContext);
  const projection = useProjection();

  const exitDrawModeCallbackRef = React.useRef(exitDrawModeCallback);
  const updateGeojsonRef = React.useRef(updateGeojson);
  const onViewOnlyFeatureClickRef = React.useRef(onViewOnlyFeatureClick);
  const onNoFeatureClickRef = React.useRef(onNoFeatureClick);
  exitDrawModeCallbackRef.current = exitDrawModeCallback;
  updateGeojsonRef.current = updateGeojson;
  onViewOnlyFeatureClickRef.current = onViewOnlyFeatureClick;
  onNoFeatureClickRef.current = onNoFeatureClick;

  React.useEffect(() => {
    if (!map || !isInEditMode) {
      return (): void => {};
    }

    const selectionType =
      geojson?.features[selectedFeatureIndex]?.properties?.selectionType;
    const actualDrawMode =
      drawMode || selectionTypeToDrawModeValue(selectionType);

    if (actualDrawMode === DRAWMODE.SPLIT) {
      const viewport = map.getViewport();
      if (viewport?.style) {
        viewport.style.cursor = 'crosshair';
      }
    }

    const selectedFeature = geojson
      ? geojson.features[selectedFeatureIndex]
      : null;

    const drawOperation = createDrawOperation(
      actualDrawMode,
      selectedFeature?.geometry.type,
    );
    const drawStyle = drawStyles[actualDrawMode];
    const modifyStyle = modifyStyles[actualDrawMode];

    const geojsonFormatter = new OlGeoJSON({
      featureProjection: projection,
    });

    const allFeatures = geojson ? geojsonFormatter.readFeatures(geojson) : [];

    const viewOnlyFeatures = allFeatures.filter(
      (_f, i) => i !== selectedFeatureIndex,
    );
    const viewOnlySource = new VectorSource({ features: viewOnlyFeatures });
    const viewOnlyLayer = new VectorLayer({
      source: viewOnlySource,
      style: viewOnlyStyle,
      zIndex: 1000000 - 1,
    });
    const editFeatureCollection = new Collection(
      allFeatures[selectedFeatureIndex]
        ? [allFeatures[selectedFeatureIndex]]
        : [],
    );

    const source = new VectorSource({ features: editFeatureCollection });

    const selectedFeatureHasGeometry =
      selectedFeature && !isGeoJSONGeometryEmpty(selectedFeature);

    const layer = new VectorLayer({
      source,
      style: modifyStyle,
      zIndex: 1000000,
    });

    map.addLayer(layer);
    map.addLayer(viewOnlyLayer);

    const interactions: Interaction[] = [];
    const handleSingleClick =
      (onViewOnlyFeatureClickRef.current || onNoFeatureClickRef.current) &&
      ((event: { pixel: Pixel }): void => {
        const features: Feature<Geometry>[] = [];

        map.forEachFeatureAtPixel(
          event.pixel,
          (featureAtPixel, layerAtPixel) => {
            if (layerAtPixel === viewOnlyLayer || layerAtPixel === layer) {
              features.push(featureAtPixel as Feature<Geometry>);
            }
          },
          {
            layerFilter: (layerAtPixel) =>
              layerAtPixel === viewOnlyLayer || layerAtPixel === layer,
          },
        );

        if (features.length) {
          onViewOnlyFeatureClickRef.current?.(features);
        } else {
          onNoFeatureClickRef.current?.();
        }
      });

    if (handleSingleClick) {
      map.on('singleclick', handleSingleClick);
    }

    const updateFeature = (feature: Feature<Geometry>): void => {
      // Convert Circle to Polygon for GeoJSON serialization
      if (feature.getGeometry()?.getType() === 'Circle') {
        const geometry = fromCircle(feature.getGeometry() as Circle, 64);
        feature.setGeometry(geometry);
      }
      const obj = geojsonFormatter.writeFeatureObject(feature);

      if (obj.geometry && 'coordinates' in obj.geometry) {
        obj.geometry.coordinates = roundCoordinates(obj.geometry.coordinates);
      }

      // copy properties
      obj.properties = {
        ...selectedFeature?.properties,
      };

      const newGeoJson = {
        ...geojson,
        features: [
          ...geojson.features.slice(0, selectedFeatureIndex),
          obj,
          ...geojson.features.slice(selectedFeatureIndex + 1),
        ],
      };
      updateGeojsonRef.current?.(newGeoJson, NEW_FEATURE_CREATED);
    };

    const splitSelectedPolygon = (splitFeature: Feature<Geometry>): void => {
      const isValidSplit =
        selectedFeatureHasGeometry && selectedFeature.geometry.type !== 'Point';

      if (!isValidSplit) {
        return;
      }

      // GeoJsons are always treated as latlon, therefore using EPSG:4326 as transformer does not do anything except translating the object.
      const geojsonFormatterNoTransform = new OlGeoJSON({
        featureProjection: 'EPSG:4326',
      });
      const isLineSplit = selectedFeature.geometry.type === 'LineString';

      // Splitting is done in coordinate system of the map to provide less artifacts. For example splitting in globe projection 32661 will otherwise not work.
      const splitGeoJson =
        geojsonFormatterNoTransform.writeFeatureObject(splitFeature);

      const selectedFeatureOrgCoordSpace =
        geojsonFormatterNoTransform.writeFeatureObject(
          geojsonFormatter.readFeature(selectedFeature) as Feature<Geometry>,
        );

      const splitResult = isLineSplit
        ? splitLine(
            selectedFeatureOrgCoordSpace as GeoJSON.Feature<GeoJSON.LineString>,
            splitGeoJson as GeoJSON.Feature<GeoJSON.LineString>,
          )
        : splitPolygon(
            selectedFeatureOrgCoordSpace as GeoJSON.Feature<GeoJSON.Polygon>,
            splitGeoJson as GeoJSON.Feature<GeoJSON.Polygon>,
          );

      if (!splitResult) {
        console.warn('no split result');
        return;
      }

      // Transform back to orig
      const splitResultAsGeoJson = geojsonFormatter.writeFeaturesObject(
        geojsonFormatterNoTransform.readFeatures(splitResult),
      );

      // Assemble the result.
      const newGeoJson = {
        ...geojson,
        features: [
          ...geojson.features.slice(0, selectedFeatureIndex),
          ...splitResultAsGeoJson.features,
          ...geojson.features.slice(selectedFeatureIndex + 1),
        ],
      };

      updateGeojson?.(newGeoJson, NEW_FEATURE_CREATED);
    };

    // Draw functionality, for drawing new polygons/linestrings/boxes/markers
    const draw = new Draw({
      source,
      traceSource: source,
      style: drawStyle,
      ...drawOperation,
    });

    let drawingFinishedBy: DrawModeExitCallback = 'escaped'; // default

    draw.on('drawabort', () => {
      exitDrawModeCallbackRef.current?.('escaped');
    });

    let pointerDownPx: [number, number] | null = null;
    const recordPointerDownPosition = (event: PointerEvent): void => {
      pointerDownPx = [event.clientX, event.clientY];
    };

    const handleClickNearPolygonStartPoint = (event: MouseEvent): void => {
      if (actualDrawMode !== DRAWMODE.POLYGON) {
        return;
      }

      if (isClickFromDrag(event, pointerDownPx)) {
        return;
      }

      const resolution = map?.getView()?.getResolution();
      const threshold = resolution ? 10 * resolution : 0;

      const clickCoords = map.getEventCoordinate(event);

      const sketchFeature = draw?.getOverlay()?.getSource()?.getFeatures()[0];
      if (sketchFeature) {
        const geometry = sketchFeature.getGeometry();
        if (geometry) {
          const { flatCoordinates } = geometry;
          // Need ≥3 placed vertices to finalize a polygon (ring [v1,v2,v3,cursor,v1] = 10 flat coords).
          if (!flatCoordinates || flatCoordinates.length < 10) {
            return;
          }

          const firstPoint = [flatCoordinates[0], flatCoordinates[1]];
          const distance = Math.sqrt(
            (clickCoords[0] - firstPoint[0]) ** 2 +
              (clickCoords[1] - firstPoint[1]) ** 2,
          );
          if (distance <= threshold) {
            draw.finishDrawing();
            drawingFinishedBy = 'clickedNearStartingPoint';
            exitDrawModeCallbackRef.current?.('clickedNearStartingPoint');
          }
        }
      }
    };

    draw.on('drawstart', () => {
      map
        .getViewport()
        .addEventListener('click', handleClickNearPolygonStartPoint);
      if (
        onViewOnlyFeatureClickRef.current ||
        actualDrawMode === DRAWMODE.SPLIT
      ) {
        map.getViewport().style.cursor = '';
      }
      map
        .getViewport()
        .addEventListener('pointerdown', recordPointerDownPosition);
    });

    // Handle doubleClick instead of shiftClick
    const handleDoubleClickToClosePolygon = (event: MouseEvent): void => {
      if (event.detail === 2 && event.button === 0) {
        // left double click
        event.preventDefault();

        // Need ≥3 placed vertices to finalize a polygon (ring [v1,v2,v3,cursor,v1] = 10 flat coords).
        if (actualDrawMode === DRAWMODE.POLYGON) {
          const sketchFeature = draw
            ?.getOverlay()
            ?.getSource()
            ?.getFeatures()[0];
          const flatCoordinates = sketchFeature?.getGeometry()?.flatCoordinates;
          if (!flatCoordinates || flatCoordinates.length < 10) {
            return;
          }
        }

        drawingFinishedBy = 'doubleClicked';
        draw.finishDrawing();
      }
    };

    map
      .getViewport()
      .addEventListener('dblclick', handleDoubleClickToClosePolygon);

    draw.on('drawend', (evt: DrawEvent): void => {
      if (actualDrawMode === DRAWMODE.SPLIT) {
        splitSelectedPolygon(evt.feature);
      } else {
        updateFeature(evt.feature);
      }

      if (
        actualDrawMode === DRAWMODE.POINT ||
        actualDrawMode === DRAWMODE.LOCATION
      ) {
        // Point/location tools complete on the first click, so drawend should
        // exit edit mode immediately instead of waiting for an explicit escape.
        exitDrawModeCallbackRef.current?.('completed');
      } else if (drawingFinishedBy === 'escaped') {
        // Only exit on escape for multi-click draw modes.

        exitDrawModeCallbackRef.current?.('escaped');
      }
      drawingFinishedBy = 'escaped';
    });

    let lastPointerEvent: PointerEvent | null = null;

    const trackPointerMove = (event: PointerEvent): void => {
      lastPointerEvent = event;

      if (onViewOnlyFeatureClick && actualDrawMode !== DRAWMODE.SPLIT) {
        const pixel = map.getEventPixel(event);
        let hasViewOnlyHit = false;

        map.forEachFeatureAtPixel(
          pixel,
          (_featureAtPixel, layerAtPixel) => {
            if (layerAtPixel === viewOnlyLayer) {
              hasViewOnlyHit = true;
            }
          },
          {
            layerFilter: (layerAtPixel) => layerAtPixel === viewOnlyLayer,
          },
        );

        map.getViewport().style.cursor = hasViewOnlyHit ? 'pointer' : '';
      }

      // Update which rotate handle is "active" so only that
      // icon is rendered near the mouse when editing boxes/squares.
      // While rotating, keep the originally clicked handle active.
      if (isBoxOrSquare && selectedFeatureHasGeometry && !isRotating) {
        const featureToRotate = editFeatureCollection.item(0) as
          | Feature<Geometry>
          | undefined;
        if (!featureToRotate) {
          return;
        }

        const resolution = map.getView().getResolution();
        const handlePoints = getBoxRotateHandlePoints(
          featureToRotate,
          resolution,
        );

        const prevIndex = featureToRotate.get('activeRotateHandleIndex');
        let newIndex: number | undefined;

        if (!handlePoints || !resolution || !isBoxOrSquareRotatable) {
          newIndex = undefined;
        } else {
          const coord = map.getEventCoordinate(event);
          const threshold = 18 * resolution; // enlarge hover hit area for rotate handle

          let closestIndex = -1;
          let closestDist = Infinity;

          handlePoints.forEach(([hx, hy], idx) => {
            const dx = coord[0] - hx;
            const dy = coord[1] - hy;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < closestDist) {
              closestDist = dist;
              closestIndex = idx;
            }
          });

          newIndex = closestDist <= threshold ? closestIndex : undefined;
        }

        if (prevIndex !== newIndex) {
          if (newIndex === undefined) {
            featureToRotate.unset('activeRotateHandleIndex', true);
          } else {
            featureToRotate.set('activeRotateHandleIndex', newIndex, true);
          }
          layer.changed();
        }
      }
    };

    map.getViewport().addEventListener('pointermove', trackPointerMove);

    const deleteVertexOnDeleteKey = (event: KeyboardEvent): void => {
      if (
        event.key === 'Delete' &&
        lastPointerEvent &&
        selectedFeatureHasGeometry
      ) {
        event.preventDefault();
        const coordinate = map.getEventCoordinate(lastPointerEvent);
        if (modify && map.getInteractions().getArray().includes(modify)) {
          modify.removePoint(coordinate);
        }
      }
    };

    window.addEventListener('keydown', deleteVertexOnDeleteKey);

    const isSquare = actualDrawMode === DRAWMODE.SQUARE;
    const isBoxOrSquareRotatable =
      actualDrawMode === DRAWMODE.ROTATABLEBOX || isSquare;
    const isBoxOrSquare =
      actualDrawMode === DRAWMODE.BOX || isBoxOrSquareRotatable;

    // Modifying is a combination of Modify and Translate interactions
    const modify = new Modify({
      features: editFeatureCollection,
      insertVertexCondition: !isBoxOrSquare ? always : never,
      style: isSquare ? [] : modifyHandleStyle,
    });

    if (isBoxOrSquare) {
      // Box is a special case:
      // - Use a custom style to present the user with a box when dragging (even though the temp polygon is changing against box conditions)
      // - When modification is done, define the box using the dragged corner and its opposite corner

      modify.on('modifystart', (evt) => {
        const feature = evt.features.getArray()[0];
        // Mark that we are modifying the box so rotate handles
        // can be hidden while dragging.
        feature.set('suppressRotateHandle', true, true);
        feature.set('originalGeometry', feature.getGeometry()?.clone(), true);

        const viewport = map.getViewport();
        if (viewport?.style) {
          viewport.style.cursor = 'grabbing';
        }
      });

      modify.on('modifyend', (evt: ModifyEvent) => {
        const feature = evt.features.getArray()[0];

        const boxPolygon = createBoxBasedOnModifiedVertex(
          feature.getGeometry() as Polygon,
          feature.get('originalGeometry') as Polygon,
          isSquare,
        );
        feature.setGeometry(boxPolygon);

        // Re-enable rotate handle visibility after modifying.
        feature.unset('suppressRotateHandle', true);

        const viewport = map.getViewport();
        if (viewport?.style) {
          viewport.style.cursor = '';
        }

        updateFeature(feature);
      });
    } else {
      modify.on('modifystart', () => {
        const viewport = map.getViewport();
        if (viewport?.style) {
          viewport.style.cursor = 'grabbing';
        }
      });

      modify.on('modifyend', (evt: ModifyEvent) => {
        const viewport = map.getViewport();
        if (viewport?.style) {
          viewport.style.cursor = '';
        }

        updateFeature(evt.features.getArray()[0]);
      });
    }

    const translate = new Translate({
      features: editFeatureCollection,
    });

    translate.on('translateend', (evt: ModifyEvent) => {
      updateFeature(evt.features.getArray()[0]);
    });

    // Rotation handling for boxes and squares using the visual rotate handles
    let isRotating = false;
    let rotationCenter: [number, number] | null = null;
    let rotationStartAngle = 0;
    let originalRing: number[][] | null = null;
    let disabledDragPans: DragPan[] = [];

    // Track when the user has pressed down near a resize corner so we can
    // immediately show a grabbing cursor on mousedown (before any movement).
    let isPointerDownNearCorner = false;

    const handlePointerDownNearCorner = (event: PointerEvent): void => {
      if (!isBoxOrSquare || !selectedFeatureHasGeometry) {
        return;
      }

      const feature = editFeatureCollection.item(0) as
        | Feature<Geometry>
        | undefined;
      const geom = feature?.getGeometry();
      if (!geom || geom?.getType() !== 'Polygon') {
        return;
      }

      const polygon = geom as Polygon;
      const ring = polygon.getCoordinates()[0];
      if (!ring || ring.length < 4) {
        return;
      }

      const clickCoord = map.getEventCoordinate(event);
      const resolution = map.getView().getResolution();
      if (!resolution) {
        return;
      }

      const threshold = 10 * resolution;

      let closestDist = Infinity;

      ring.forEach(([vx, vy]) => {
        const dx = clickCoord[0] - vx;
        const dy = clickCoord[1] - vy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < closestDist) {
          closestDist = dist;
        }
      });

      if (closestDist <= threshold) {
        const viewport = map.getViewport();
        if (viewport?.style) {
          viewport.style.cursor = 'grabbing';
        }
        isPointerDownNearCorner = true;
      }
    };

    const handlePointerUpAfterCorner = (): void => {
      if (!isPointerDownNearCorner && !isRotating) {
        return;
      }

      const viewport = map.getViewport();
      if (viewport?.style) {
        viewport.style.cursor = '';
      }

      isPointerDownNearCorner = false;
    };

    const startRotateIfOnHandle = (event: PointerEvent): void => {
      if (!isBoxOrSquareRotatable || !selectedFeatureHasGeometry) {
        return;
      }

      const featureToRotate = editFeatureCollection.item(
        0,
      ) as Feature<Geometry>;
      const geom = featureToRotate?.getGeometry();
      if (!geom || geom?.getType() !== 'Polygon') {
        return;
      }

      const polygon = geom as Polygon;
      const ring = polygon.getCoordinates()[0];
      if (!ring || ring.length < 4) {
        return;
      }

      // Exclude possible closing coordinate
      const ringWithoutClose =
        ring.length > 4 &&
        ring[0][0] === ring[ring.length - 1][0] &&
        ring[0][1] === ring[ring.length - 1][1]
          ? ring.slice(0, ring.length - 1)
          : ring;

      const clickCoord = map.getEventCoordinate(event);
      const resolution = map.getView().getResolution();
      if (!resolution) {
        return;
      }

      // Use the same helper for handle locations as the style code,
      // so visual position and click area are consistent.
      const handlePoints = getBoxRotateHandlePoints(
        featureToRotate,
        resolution,
      );
      if (!handlePoints) {
        return;
      }
      const threshold = 12 * resolution; // enlarge click hit area for rotate handle

      // Determine which handle was clicked (nearest within threshold)
      let closestIndex = -1;
      let closestDist = Infinity;

      handlePoints.forEach(([hx, hy], idx) => {
        const dx = clickCoord[0] - hx;
        const dy = clickCoord[1] - hy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < closestDist) {
          closestDist = dist;
          closestIndex = idx;
        }
      });

      if (closestIndex === -1 || closestDist > threshold) {
        return;
      }

      // Lock the active rotate handle index so its icon stays visible
      const handleFeature = editFeatureCollection.item(0) as
        | Feature<Geometry>
        | undefined;
      if (handleFeature) {
        handleFeature.set('activeRotateHandleIndex', closestIndex, true);
        layer.changed();
      }

      isRotating = true;

      // Temporarily disable map panning while rotating so the
      // underlying map does not move when dragging the rotate handle.
      disabledDragPans = [];
      map.getInteractions().forEach((interaction) => {
        if (interaction instanceof DragPan && interaction.getActive()) {
          disabledDragPans.push(interaction as DragPan);
          interaction.setActive(false);
        }
      });
      // Recompute box center from the current polygon
      let minX = ringWithoutClose[0][0];
      let maxX = ringWithoutClose[0][0];
      let minY = ringWithoutClose[0][1];
      let maxY = ringWithoutClose[0][1];

      ringWithoutClose.forEach(([x, y]) => {
        if (x < minX) {
          minX = x;
        }
        if (x > maxX) {
          maxX = x;
        }
        if (y < minY) {
          minY = y;
        }
        if (y > maxY) {
          maxY = y;
        }
      });

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      rotationCenter = [centerX, centerY];
      rotationStartAngle = Math.atan2(
        clickCoord[1] - centerY,
        clickCoord[0] - centerX,
      );
      originalRing = ring.map(([x, y]) => [x, y]);

      // While rotating, show a grabbing cursor on the map viewport
      const viewport = map.getViewport();
      if (viewport?.style) {
        viewport.style.cursor = 'grabbing';
      }

      // While rotating, disable modify/translate to avoid conflicts
      modify.setActive(false);
      translate.setActive(false);

      event.preventDefault();
    };

    const rotateOnPointerMove = (event: PointerEvent): void => {
      if (!isRotating || !rotationCenter || !originalRing) {
        return;
      }

      const featureToRotate = editFeatureCollection.item(0) as
        | Feature<Geometry>
        | undefined;
      const geom = featureToRotate?.getGeometry();
      if (!geom || geom?.getType() !== 'Polygon') {
        return;
      }

      const polygon = geom as Polygon;

      const [centerX, centerY] = rotationCenter;
      const coord = map.getEventCoordinate(event);
      const angle = Math.atan2(coord[1] - centerY, coord[0] - centerX);
      const delta = angle - rotationStartAngle;

      const cos = Math.cos(delta);
      const sin = Math.sin(delta);

      const rotatedRing = originalRing.map(([x, y]) => {
        const dx = x - centerX;
        const dy = y - centerY;
        const newX = centerX + dx * cos - dy * sin;
        const newY = centerY + dx * sin + dy * cos;
        return [newX, newY];
      });

      if (rotatedRing.length > 0) {
        // Ensure the ring is closed
        const [firstX, firstY] = rotatedRing[0];
        rotatedRing[rotatedRing.length - 1] = [firstX, firstY];
      }

      polygon.setCoordinates([rotatedRing]);
    };

    const endRotate = (): void => {
      if (!isRotating) {
        return;
      }

      isRotating = false;
      rotationCenter = null;
      rotationStartAngle = 0;
      originalRing = null;

      // Re-enable any drag-pan interactions we disabled when rotation
      // started so normal map panning works again.
      disabledDragPans.forEach((interaction) => interaction.setActive(true));
      disabledDragPans = [];

      // Restore the default cursor when rotation stops
      const viewport = map.getViewport();
      if (viewport?.style) {
        viewport.style.cursor = '';
      }

      modify.setActive(true);
      translate.setActive(true);

      const featureToRotate = editFeatureCollection.item(0) as
        | Feature<Geometry>
        | undefined;
      if (featureToRotate) {
        updateFeature(featureToRotate);
        // Clear active handle so hover logic can take over again
        featureToRotate.unset('activeRotateHandleIndex', true);
        layer.changed();
      }
    };

    const abortOnEsc = (evt: KeyboardEvent): void => {
      if (evt.key === 'Escape') {
        evt.preventDefault();
        draw.finishDrawing();
        if (selectedFeatureHasGeometry) {
          exitDrawModeCallbackRef.current?.('escaped');
        } else {
          draw.abortDrawing();
        }
      }
    };
    const onlyAllowMoving =
      actualDrawMode === DRAWMODE.CIRCLE ||
      actualDrawMode === DRAWMODE.OVAL ||
      actualDrawMode === DRAWMODE.FREEHANDLINE;

    if (actualDrawMode === DRAWMODE.SPLIT) {
      // In split mode, draw a splitting line over the existing polygon;
      // we don't expose modify/translate interactions here.
      interactions.push(draw);
    } else if (selectedFeatureHasGeometry) {
      if (onlyAllowMoving) {
        interactions.push(translate); // Only allow moving
      } else {
        interactions.push(translate, modify); // Allow moving and vertex editing
      }
    } else {
      interactions.push(draw);
    }

    interactions.push(new Snap({ source: viewOnlySource }));

    if (selectedFeatureHasGeometry) {
      interactions.push(
        new Snap({
          source,
        }),
      );
    }

    if (isBoxOrSquare && selectedFeatureHasGeometry) {
      const viewport = map.getViewport();
      viewport.addEventListener('pointerdown', startRotateIfOnHandle);
      viewport.addEventListener('pointermove', rotateOnPointerMove);
      viewport.addEventListener('pointerup', endRotate);

      // Ensure the grabbing cursor appears immediately when pressing on a
      // resize corner, not only after the pointer starts moving.
      viewport.addEventListener('pointerdown', handlePointerDownNearCorner);
      viewport.addEventListener('pointerup', handlePointerUpAfterCorner);
    }

    window.addEventListener('keydown', abortOnEsc);

    interactions.forEach((i) => map.addInteraction(i));

    return (): void => {
      map
        .getViewport()
        .removeEventListener('dblclick', handleDoubleClickToClosePolygon);
      map
        .getViewport()
        .removeEventListener('click', handleClickNearPolygonStartPoint);
      map
        .getViewport()
        .removeEventListener('pointerdown', recordPointerDownPosition);

      window.removeEventListener('keydown', abortOnEsc);
      interactions.forEach((i) => map.removeInteraction(i));
      map.removeLayer(layer);
      map.removeLayer(viewOnlyLayer);
      map.getViewport().removeEventListener('pointermove', trackPointerMove);
      if (isBoxOrSquare && selectedFeatureHasGeometry) {
        const viewport = map.getViewport();
        viewport.removeEventListener('pointerdown', startRotateIfOnHandle);
        viewport.removeEventListener('pointermove', rotateOnPointerMove);
        viewport.removeEventListener('pointerup', endRotate);
        viewport.removeEventListener(
          'pointerdown',
          handlePointerDownNearCorner,
        );
        viewport.removeEventListener('pointerup', handlePointerUpAfterCorner);
      }
      window.removeEventListener('keydown', deleteVertexOnDeleteKey);
      if (handleSingleClick) {
        map.un('singleclick', handleSingleClick);
      }
      if (onViewOnlyFeatureClickRef.current) {
        map.getViewport().style.cursor = '';
      }
    };
    // Callback props are accessed via refs (above) so the effect doesn't
    // re-run when parents pass inline arrows. Re-running mid-sketch would
    // tear down the Draw interaction and lose the in-progress polygon.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    isInEditMode,
    geojson,
    projection,
    viewOnlyStyle,
    selectedFeatureIndex,
    drawMode,
  ]);

  return null;
};
